import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  HttpLink,
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
} from "@apollo/client";
import { LocalState } from "@apollo/client/local-state";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { OperationTypeNode } from "graphql";
import type { Client as GraphQLWsClient } from "graphql-ws";
import { createClient } from "graphql-ws";
import config from "@/config.ts";
import { log } from "@/config/logging";
import {
  getOidcUserStorageKey,
  oidcUserMemoryStorage,
} from "@/provider/AuthProvider/oidcUserStore";
import { getIamAccountIdFromUserToken } from "@/provider/AuthProvider/accountAuthorize";
import { SetContextLink } from "@apollo/client/link/context";
import { User } from "oidc-client-ts";
import { ErrorLink } from "@apollo/client/link/error";
import { useRouteDealerCdhAccountId } from "@/hooks/useRouteDealerCdhAccountId";
import { showForbiddenApiToastIfApplicable } from "@/utils/forbiddenApiError";
import { showSupportSessionTimeoutIfApplicable } from "@/utils/supportSessionTimeoutError";
import { RemoveTypenameFromVariablesLink } from "@apollo/client/link/remove-typename";
import { AccountCacheLink } from "./AccountCacheLink";
import { BatchIncrementalLink } from "./BatchIncrementalLink";
import { DebugIncrementalLink } from "./DebugIncrementalLink";
import { graphqlHttpUri } from "./graphqlHttpUri";
import { ResilientIncrementalHandler } from "./ResilientIncrementalHandler";
import { StripMockedLink } from "./StripMockedLink";
import { ANONYMOUS_FIELDS } from "@/gql/anonymous-fields";
import { getSupportModeImpersonationHeadersForAccount } from "@/context/supportMode/supportModeAuthHeaders";
import { getDxpIamAuthHeader } from "@/store/dxpIamAuthHeader";
import i18n from "@/config/i18n";

// Local `@defer` tuning — flip while testing, don't commit enabled.
function deferApolloTuning(): {
  batchIncremental: boolean;
  debugMultipart: boolean;
} {
  return {
    batchIncremental: true,
    debugMultipart: false,
  };
}

const deferTuning = deferApolloTuning();

function getUser() {
  const oidcStorage = oidcUserMemoryStorage.getItem(getOidcUserStorageKey());
  if (oidcStorage === null) {
    return null;
  }

  return User.fromStorageString(oidcStorage);
}

export function getDxpAuthHeaders(
  accountId?: string | null,
  operationName?: string,
  skipDxpIamHeader = false
): Record<string, string> {
  const headers: Record<string, string> = {
    "Accept-Language": i18n.resolvedLanguage ?? i18n.language,
  };
  const user = getUser();
  const token = user?.access_token;

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (accountId !== null && accountId !== undefined && accountId.length > 0) {
    headers["dxp-account-ids"] = accountId;
    Object.assign(
      headers,
      getSupportModeImpersonationHeadersForAccount(accountId)
    );
  }

  if (!skipDxpIamHeader) {
    Object.assign(
      headers,
      getDxpIamAuthHeader(getIamAccountIdFromUserToken(user), operationName)
    );
  }

  return headers;
}

export type ApolloForbiddenToastContext = {
  skipGlobalError?: boolean;
};

function createApolloClient() {
  const httpLink = new HttpLink({
    uri: ({ operationName }) =>
      graphqlHttpUri(config.BSYS_DXP_GQL, operationName),
    headers: { "content-type": "application/json; charset=utf-8" },
  });

  const errorLink = new ErrorLink(({ error, operation }) => {
    const context = operation.getContext() as ApolloForbiddenToastContext;

    showSupportSessionTimeoutIfApplicable(error);

    if (context.skipGlobalError !== true) {
      showForbiddenApiToastIfApplicable(error);
    }

    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ path }) => {
        log.warn("GraphQL error", {
          attributes: {
            operationName: operation.operationName ?? "unknown",
            path: path?.map(String).join(".") ?? "unknown",
          },
        });
      });
    } else if (CombinedProtocolErrors.is(error)) {
      log.warn("GraphQL protocol error", {
        attributes: {
          operationName: operation.operationName ?? "unknown",
        },
      });
    } else {
      log.error(error, {
        message: "GraphQL network error",
        attributes: {
          operationName: operation.operationName ?? "unknown",
        },
      });
    }
  });

  const cache = new InMemoryCache({
    typePolicies: {
      ContractStatusChange: { keyFields: ["contractId"] },
      DealerCustomerOrganization: { keyFields: ["id"] },
      Organization: { keyFields: ["id"] },
      DealerCustomer: {
        fields: {
          organization: {
            merge: true,
          },
        },
      },
      // LicenseBreakdown is an embedded (non-normalized) object on ContractLine.
      // Different queries select disjoint slices (`licenses` vs.
      // `assignedSeats`/`unassignedSeats`), so without an explicit merge Apollo
      // would replace the embedded value on every write and trigger partial-cache
      // refetches of any active query that reads the dropped fields.
      LicenseBreakdown: { merge: true },
    },
  });

  const accountCacheLink = new AccountCacheLink({
    anonymousFields: ANONYMOUS_FIELDS,
    cache,
    enabled: true,
  });

  const authLink = new SetContextLink((prevContext, operation) => {
    const skipDxpIamHeader = prevContext.skipDxpIamHeader === true;
    const accountId =
      prevContext.dxpAccountIdOverride ?? accountCacheLink.currentAccountId;

    return {
      headers: {
        ...getDxpAuthHeaders(
          accountId,
          operation.operationName,
          skipDxpIamHeader
        ),
        ...prevContext.headers,
      },
    };
  });

  const debugIncrementalLink = new DebugIncrementalLink({
    enabled: deferTuning.debugMultipart,
  });

  const restLink = deferTuning.debugMultipart
    ? authLink.concat(debugIncrementalLink, httpLink)
    : authLink.concat(httpLink);

  const wsClient = createClient({
    url: config.BSYS_DXP_GQL,
    connectionParams: () => {
      return getDxpAuthHeaders(accountCacheLink.currentAccountId);
    },
  });

  const subscriptionLink = new GraphQLWsLink(wsClient);

  const link = ApolloLink.split(
    ({ operationType }) => {
      return operationType === OperationTypeNode.SUBSCRIPTION;
    },
    subscriptionLink,
    restLink
  );

  const removeTypeNameVariablesLink = new RemoveTypenameFromVariablesLink();

  const stripMockedLink = new StripMockedLink();

  const batchIncrementalLink = deferTuning.batchIncremental
    ? new BatchIncrementalLink()
    : null;

  const apolloClient = new ApolloClient({
    link: ApolloLink.from([
      ...(batchIncrementalLink ? [batchIncrementalLink] : []),
      removeTypeNameVariablesLink,
      accountCacheLink,
      stripMockedLink,
      errorLink,
      link,
    ]),

    cache,

    /*
  Inserted by Apollo Client 3->4 migration codemod.
  If you are not using the `@client` directive in your application,
  you can safely remove this option.
  */
    localState: new LocalState({}),

    /*
  Inserted by Apollo Client 3->4 migration codemod.
  If you are not using the `@defer` directive in your application,
  you can safely remove this option.
  */
    incrementalHandler: new ResilientIncrementalHandler(),
  });

  return {
    client: apolloClient,
    accountCacheLink,
    wsClient,
  };
}

let singleton:
  | {
      client: ApolloClient;
      accountCacheLink: AccountCacheLink;
      wsClient: GraphQLWsClient;
    }
  | undefined;

function getOrCreateApolloClient() {
  singleton ??= createApolloClient();
  return singleton;
}

/** Returns the singleton Apollo client outside of React (e.g. app startup). */
function getApolloClient() {
  return getOrCreateApolloClient().client;
}

const useApolloClient = () => {
  const accountId = useRouteDealerCdhAccountId();
  const { client, accountCacheLink, wsClient } = getOrCreateApolloClient();

  // Switch during render so the cache is correct before children mount
  // (AccountProvider keys on accountId). switchAccount updates
  // currentAccountId synchronously, so Strict Mode won't swap twice.
  if (accountCacheLink.currentAccountId !== accountId) {
    accountCacheLink.switchAccount(
      accountCacheLink.currentAccountId,
      accountId
    );
    // Force the WebSocket to reconnect so connectionParams picks up the
    // new account ID. terminate() is non-fatal; the client auto-reconnects.
    wsClient.terminate();
  }
  return client;
};

/** Destroys the singleton so the next call creates a fresh client and cache. */
function resetApolloClient() {
  if (singleton) {
    singleton.wsClient.dispose();
    singleton.client.stop();
  }
  singleton = undefined;
}

/** WS auth is set at connect time; after token refresh, reconnect so connectionParams sends the new bearer token. */
function reconnectApolloWebSocket() {
  getOrCreateApolloClient().wsClient.terminate();
}

export {
  useApolloClient,
  resetApolloClient,
  getApolloClient,
  reconnectApolloWebSocket,
};
